import { Alert, Button, Grid, Input, Segmented, Space, Table, Typography } from "antd";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../app/api.ts";
import { formatSchoolDateTime } from "../app/time.ts";
import { telegramKeys, type GuardianReachItem, type GuardianReachState } from "../app/telegram.ts";

const PAGE_SIZE = 25;
const states: GuardianReachState[] = ["Unlinked", "Unreachable", "NeedsReview"];

/**
 * Guardians of enrolled students the school cannot reach on Telegram (story 119): not linked yet, or linked but the
 * bot's messages no longer arrive; and those whose link was made from a phone since removed from their profile, which
 * staff confirm or unlink on the family card (bd-3qg.17). From GET /telegram/guardians; requires people.read.
 */
export function TelegramReachPage() {
  const { t } = useTranslation();
  const screens = Grid.useBreakpoint();
  const [state, setState] = useState<GuardianReachState>("Unlinked");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const filters = { state, search, page };

  const query = useQuery({
    queryKey: telegramKeys.reach(filters),
    queryFn: async () => {
      const { data, error } = await api.GET("/telegram/guardians", {
        params: { query: { State: state, Page: page, PageSize: PAGE_SIZE, ...(search ? { Search: search } : {}) } },
      });
      if (error || !data) throw new Error(t("app.error"));
      return data;
    },
    placeholderData: keepPreviousData,
  });

  const columns = [
    {
      key: "guardian",
      title: t("telegram.reach.columns.guardian"),
      render: (_: unknown, g: GuardianReachItem) => <Typography.Text strong>{g.fullName}</Typography.Text>,
    },
    {
      key: "family",
      title: t("telegram.reach.columns.family"),
      render: (_: unknown, g: GuardianReachItem) => <Link to={`/families/${g.familyId}`}>{g.familyName}</Link>,
    },
    {
      key: "phone",
      title: t("telegram.reach.columns.phone"),
      width: 160,
      render: (_: unknown, g: GuardianReachItem) => <a href={`tel:${g.phone}`}>{g.phone}</a>,
    },
    ...(state === "Unreachable"
      ? [
          {
            key: "since",
            title: t("telegram.reach.columns.since"),
            width: 170,
            render: (_: unknown, g: GuardianReachItem) => (g.unreachableSince ? formatSchoolDateTime(g.unreachableSince) : null),
          },
        ]
      : []),
    ...(state === "NeedsReview"
      ? [
          {
            key: "reviewPhone",
            title: t("telegram.reach.columns.reviewPhone"),
            width: 160,
            render: (_: unknown, g: GuardianReachItem) => g.reviewPhone,
          },
          {
            key: "reviewSince",
            title: t("telegram.reach.columns.reviewSince"),
            width: 170,
            render: (_: unknown, g: GuardianReachItem) => (g.needsReviewSince ? formatSchoolDateTime(g.needsReviewSince) : null),
          },
        ]
      : []),
  ];

  return (
    <Space orientation="vertical" size="middle" style={{ width: "100%" }}>
      <div>
        <Typography.Title level={3} style={{ margin: 0 }}>
          {t("telegram.reach.title")}
        </Typography.Title>
        <Typography.Text type="secondary">{t("telegram.reach.subtitle")}</Typography.Text>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center" }}>
        <Segmented<GuardianReachState>
          value={state}
          aria-label={t("telegram.reach.stateFilter")}
          options={states.map((s) => ({ value: s, label: t(`telegram.reach.states.${s}`) }))}
          onChange={(value) => {
            setState(value);
            setPage(1);
          }}
        />
        <Input.Search
          allowClear
          placeholder={t("telegram.reach.search")}
          aria-label={t("telegram.reach.search")}
          style={{ width: screens.md ? 300 : "100%" }}
          onSearch={(value) => {
            setSearch(value.trim());
            setPage(1);
          }}
        />
      </div>
      <Typography.Text type="secondary">{t(`telegram.reach.hints.${state}`)}</Typography.Text>
      {query.isError && <Alert type="error" showIcon title={t("app.error")} action={<Button onClick={() => void query.refetch()}>{t("app.retry")}</Button>} />}
      <Table<GuardianReachItem>
        rowKey="guardianId"
        size={screens.md ? "middle" : "small"}
        loading={query.isPending || (query.isFetching && query.isPlaceholderData)}
        columns={columns}
        dataSource={query.data?.items ?? []}
        locale={{ emptyText: t(`telegram.reach.empty.${state}`) }}
        scroll={{ x: 560 }}
        pagination={{
          current: page,
          pageSize: PAGE_SIZE,
          total: Number(query.data?.totalCount ?? 0),
          showSizeChanger: false,
          hideOnSinglePage: true,
          showTotal: (total: number) => t("telegram.reach.total", { count: total }),
          onChange: setPage,
        }}
      />
    </Space>
  );
}
